import { useState, useRef, useLayoutEffect } from 'react';
import { createPopper } from '@popperjs/core';
import { deepMerge } from 'utils/object';

const defaultOpts = {
  modifiers: [
    {
      name: 'offset',
      options: {
        offset: [0, 8]
      }
    }
  ]
};

export function useTooltip(ref, popper, opts) {
  const options = deepMerge(defaultOpts, opts);
  const popperInstance = useRef();
  const timer = useRef(null);
  const isPopperHovered = useRef();
  const [isTooltipVisible, setIsTooltipVisible] = useState();

  useLayoutEffect(() => {
    const popperNode = popper.current;
    function onPopperEnter() {
      isPopperHovered.current = true;
    }
    let t;
    function onPopperLeave() {
      isPopperHovered.current = false;
      t = setTimeout(() => {
        popperInstance.current.destroy();
        setIsTooltipVisible(false);
      }, 300);
    }

    if (popperNode && isTooltipVisible) {
      const ro = new ResizeObserver(() => {
        popperInstance.current.update();
      });

      ro.observe(popperNode);
      popperNode.addEventListener('mouseenter', onPopperEnter);
      popperNode.addEventListener('mouseleave', onPopperLeave);
      return () => {
        ro.disconnect();
        clearTimeout(t);
        popperNode.removeEventListener('mouseenter', onPopperEnter);
        popperNode.removeEventListener('mouseleave', onPopperLeave);
      };
    }
  }, [popper, isTooltipVisible]);

  useLayoutEffect(() => {
    const node = ref.current;
    function onEnter() {
      if (timer.current) {
        clearTimeout(timer.current);
        timer.current = null;
        return;
      }

      timer.current = setTimeout(() => {
        timer.current = null;
        setIsTooltipVisible(true);
        popperInstance.current = createPopper(
          ref.current,
          popper.current,
          options
        );
      }, 300);
    }
    function onLeave() {
      if (timer.current) {
        clearTimeout(timer.current);
        timer.current = null;
        return;
      }
      timer.current = setTimeout(() => {
        timer.current = null;
        if (isPopperHovered.current) return;
        popperInstance.current.destroy();
        setIsTooltipVisible(false);
      }, 300);
    }

    // workaround for case when mouse leaves element during re-render and onLeave handler isn't call
    // if (!node.matches(':hover') && popperInstance.current) {
    //   popperInstance.current.destroy();
    //   setIsTooltipVisible(false);
    // }

    node.addEventListener('mouseenter', onEnter);
    node.addEventListener('mouseleave', onLeave);
    return () => {
      node.removeEventListener('mouseenter', onEnter);
      node.removeEventListener('mouseleave', onLeave);
    };
  }, [popper, ref, options, isTooltipVisible]);

  return { isTooltipVisible };
}
